import { Source, Layer } from 'react-map-gl';
import { FilteredLayer } from './FilteredLayer';
import { sourceProps } from './source-utils';
import type { FiltrosVisor } from '../types';
import {
  COLOR,
  fillColorParcelBase,
  makeFillColorByEstado,
  makeLineColorByFirmado,
  makeLineWidthByFirmado,
  makeIrregularBoolean,
  makeEstadoMatchExpr,
  makeReunionBucketMatchExpr,
} from '../styles';

interface ParcelasLayerProps {
  url: string;
  opciones: { semaforo?: boolean; firmado?: boolean; irregular?: boolean };
  filtros: FiltrosVisor;
  /** Resalta el contorno de una parcela (flyTo se hace en MapaParcelas). */
  highlightParcelaId?: string | null;
  /**
   * GeoJSON pre-enriquecido con properties `live_estado/live_firmado/...`.
   * Si se pasa, el `<Source>` lo usa como `data` (objeto en memoria) y los
   * filtros + paint leen via `['get', 'live_*']` — robusto y sin caveats de
   * feature-state. Si no se pasa, fallback a URL + feature-state legacy.
   */
  geojson?: GeoJSON.FeatureCollection;
  /**
   * Multiplicador de opacidad SOLO para la parcela `highlightParcelaId`
   * (0..1, default 1). El resto de las parcelas mantienen su opacidad
   * normal — el user atenúa/oculta el shape de la parcela seleccionada
   * para ver qué hay debajo en el satélite, sin perder contexto del
   * mapa global. Si no hay highlightParcelaId o shapesOpacity=1, no
   * tiene efecto.
   */
  shapesOpacity?: number;
}

/**
 * Capa de parcelas. Dos fills apilados:
 *   1. `parcelas-fill`         : relleno neutro para todas las parcelas hasta que
 *      llegue el inventario.
 *   2. `parcelas-fill-estado`  : color por estado del semáforo; encima del base.
 *
 * Source-agnóstico:
 *   - `geojson` objeto → `<Source type="geojson" data={obj}>` (modo enriquecido)
 *   - `url=*.geojson`   → geojson estatico       (modo legacy + feature-state)
 *
 * Mapbox-gl 3.x rechaza expresiones que mezclan `['get', x]` y
 * `['feature-state', x]` en el mismo coalesce — el validator silenciosamente
 * monta el Layer sin filter ni paint válidos. Por eso seleccionamos el branch
 * (`enriched` / legacy) en cada expresión via builders en `styles.ts`.
 */
export function ParcelasLayer({ url, opciones, filtros, highlightParcelaId, geojson, shapesOpacity = 1 }: ParcelasLayerProps) {
  const enriched = Boolean(geojson);
  const filtersExpr = buildFilters(filtros, enriched);

  const sourceCfg = geojson
    ? { type: 'geojson' as const, data: geojson }
    : sourceProps(url);

  const fillColorByEstado = makeFillColorByEstado(enriched) as never;
  const lineColorByFirmado = makeLineColorByFirmado(enriched) as never;
  const lineWidthByFirmado = makeLineWidthByFirmado(enriched) as never;
  const irregularBool = makeIrregularBoolean(enriched);

  // Multiplicador SOLO para la parcela seleccionada. Clamp a [0,1].
  const opacityMul = Math.max(0, Math.min(1, shapesOpacity));
  // Helper: si hay parcela seleccionada y opacityMul != 1, aplica `mul`
  // SOLO a esa feature; el resto mantiene `base`. Si no hay match (nada
  // seleccionado o slider en 100%), devuelve `base` plano.
  //
  // Usamos `match` en lugar de `case` con `==` porque con `promoteId` activo
  // el `['get', 'parcela_id']` puede devolver el valor promovido inconsistentemente
  // entre versions de mapbox-gl. `match` con `to-string` normaliza ambos lados
  // a string y compara lexicalmente — robusto.
  const perFeature = (base: number, mul = opacityMul): unknown => {
    if (!highlightParcelaId || mul === 1) return base;
    return [
      'match',
      ['to-string', ['coalesce', ['get', 'parcela_id'], '']],
      String(highlightParcelaId),
      base * mul,
      base,
    ];
  };

  return (
    <Source id="parcelas" {...sourceCfg} promoteId="parcela_id">
      <FilteredLayer
        id="parcelas-fill"
        type="fill"
        filter={filtersExpr}
        paint={{
          'fill-color': fillColorParcelBase,
          'fill-opacity': perFeature(0.5) as never,
        }}
      />

      {opciones.semaforo && (
        <FilteredLayer
          id="parcelas-fill-estado"
          type="fill"
            filter={filtersExpr}
          paint={{
            'fill-color': fillColorByEstado,
            // Anidado: primero check si es la parcela seleccionada (para
            // aplicar opacityMul); dentro de cada rama, el case por
            // irregularBool ya existente.
            'fill-opacity': (highlightParcelaId && opacityMul !== 1
              ? [
                  'match',
                  ['to-string', ['coalesce', ['get', 'parcela_id'], '']],
                  String(highlightParcelaId),
                  ['case', irregularBool, (opciones.irregular ? 0.78 : 0.7) * opacityMul, 0.68 * opacityMul],
                  ['case', irregularBool, opciones.irregular ? 0.78 : 0.7, 0.68],
                ]
              : [
                  'case',
                  irregularBool,
                  opciones.irregular ? 0.78 : 0.7,
                  0.68,
                ]) as never,
          }}
        />
      )}

      <FilteredLayer
        id="parcelas-outline"
        type="line"
        filter={filtersExpr}
        paint={{
          'line-color': lineColorByFirmado,
          'line-width': opciones.firmado ? lineWidthByFirmado : 1.4,
          'line-opacity': perFeature(1) as never,
        }}
      />

      {opciones.irregular && (
        <FilteredLayer
          id="parcelas-irregular-hatch"
          type="line"
            filter={filtersExpr}
          paint={{
            'line-color': COLOR.rose,
            'line-width': 1.4,
            'line-dasharray': [2, 1.5],
            'line-opacity': (highlightParcelaId && opacityMul !== 1
              ? [
                  'match',
                  ['to-string', ['coalesce', ['get', 'parcela_id'], '']],
                  String(highlightParcelaId),
                  ['case', irregularBool, 0.85 * opacityMul, 0],
                  ['case', irregularBool, 0.85, 0],
                ]
              : ['case', irregularBool, 0.85, 0]) as never,
          }}
        />
      )}

      {/* Privadas (cliente): border dashed teal cuando source='cliente'.
          Patrón visual distintivo del contorno regular RAN, sin conflicto
          con el rose del irregular (color diferente + dasharray distinto).
          Si la privada además está marcada irregular, ambas capas se
          dibujan superpuestas — el técnico ve ambos contornos.
          Handoff parcelas privadas v2 §9. */}
      <FilteredLayer
        id="parcelas-privada-outline"
        type="line"
        filter={filtersExpr}
        paint={{
          'line-color': COLOR.teal,
          'line-width': 1.8,
          'line-dasharray': [4, 2],
          'line-opacity': [
            'case',
            ['==', ['get', 'source'], 'cliente'], 0.9 * opacityMul,
            0,
          ] as never,
        }}
      />
      <FilteredLayer
        id="parcelas-privada-fill"
        type="fill"
        filter={filtersExpr}
        paint={{
          'fill-color': COLOR.teal,
          'fill-opacity': [
            'case',
            ['==', ['get', 'source'], 'cliente'], 0.12 * opacityMul,
            0,
          ] as never,
        }}
      />


      {highlightParcelaId ? (
        <Layer
          id="parcelas-selection-outline"
          type="line"
            filter={
            (filtersExpr != null
              ? ['all', filtersExpr, ['==', ['get', 'parcela_id'], highlightParcelaId]]
              : ['==', ['get', 'parcela_id'], highlightParcelaId]) as never
          }
          paint={{
            'line-color': COLOR.cream,
            'line-width': 3.2,
            // El outline de selección también respeta la opacidad — si el
            // user puso slider a 0, el polígono entero desaparece (incluso
            // su outline blanco), revelando el satélite limpio debajo.
            'line-opacity': opacityMul,
          }}
        />
      ) : null}
    </Source>
  );
}

/**
 * Filtro operativo P1/P2/PP. Audit del GeoJSON real (mayo 2026):
 *   - `poligono` literal numérico: 440 con `1`, 7 con `2`.
 *   - 1927 features sin valor numérico (poligono null o ausente) =
 *     "Propiedad privada" implícita (no son P1 ni P2).
 *   - `tipo_propi` y el literal "PROPIEDAD PRIVADA" no aparecen en datos.
 */
function poligonoFilterClause(literals: (1 | 2 | 'PROPIEDAD PRIVADA')[]): unknown {
  const wants = new Set(literals);
  const parts: unknown[] = [];
  if (wants.has('PROPIEDAD PRIVADA')) {
    // PP = features que NO son P1 ni P2 (poligono null/undefined).
    parts.push([
      'all',
      ['!=', ['to-number', ['coalesce', ['get', 'poligono'], 0]], 1],
      ['!=', ['to-number', ['coalesce', ['get', 'poligono'], 0]], 2],
    ]);
  }
  if (wants.has(1)) {
    parts.push(['==', ['to-number', ['coalesce', ['get', 'poligono'], 0]], 1]);
  }
  if (wants.has(2)) {
    parts.push(['==', ['to-number', ['coalesce', ['get', 'poligono'], 0]], 2]);
  }
  if (parts.length === 0) return true;
  if (parts.length === 1) return parts[0];
  return ['any', ...parts];
}

function buildFilters(f: FiltrosVisor, enriched: boolean): never | undefined {
  const clauses: unknown[] = ['all'];

  // ── Operativos (FilterBar del dashboard) ────────────────────────────
  if (f.parcela_id_in !== undefined) {
    if (f.parcela_id_in.length === 0) {
      clauses.push(['==', 1, 0]);
    } else {
      clauses.push([
        'in',
        ['to-string', ['get', 'parcela_id']],
        ['literal', f.parcela_id_in],
      ]);
    }
  }
  if (f.poligono?.length) {
    clauses.push(poligonoFilterClause(f.poligono));
  }
  if (f.estado?.length) {
    // En modo enriched lee de properties; en legacy de feature-state. NO
    // mezclar — Mapbox-gl 3.x rechaza la expresión silenciosamente.
    const labels = f.estado.length === 1 ? [f.estado[0]] : [...f.estado];
    clauses.push([
      'match',
      makeEstadoMatchExpr(enriched),
      labels,
      true,
      false,
    ]);
  }
  if (f.municipio?.length) {
    clauses.push(['in', ['upcase', ['to-string', ['get', 'municipio']]], ['literal', f.municipio]]);
  }
  if (f.reunion_bucket?.length) {
    const labels = f.reunion_bucket.length === 1 ? [f.reunion_bucket[0]] : [...f.reunion_bucket];
    clauses.push([
      'match',
      makeReunionBucketMatchExpr(enriched),
      labels,
      true,
      false,
    ]);
  }

  // ── Academicos (legacy) ────────────────────────────────────────────
  if (f.anillo?.length) {
    clauses.push(['in', ['get', 'anillo'], ['literal', f.anillo]]);
  }
  if (f.ejido?.length) {
    clauses.push(['in', ['get', 'nom_nucleo'], ['literal', f.ejido]]);
  }
  if (f.area_min_ha != null) {
    clauses.push(['>=', ['to-number', ['get', 'area_ha']], f.area_min_ha]);
  }
  if (f.area_max_ha != null) {
    clauses.push(['<=', ['to-number', ['get', 'area_ha']], f.area_max_ha]);
  }
  return clauses.length > 1 ? (clauses as never) : undefined;
}
